import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { TrophyIcon, PlayIcon, CheckCircleIcon, CalendarIcon } from 'lucide-react';
import DailyExercisesHero from '../../components/student/dailyExercises/DailyExercisesHero';
import api from '../../services/api';
import toast from 'react-hot-toast';

const Competitions = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const navigate = useNavigate();

  // حالة قائمة المسابقات
  const [competitions, setCompetitions] = useState([]);
  const [loadingCompetitions, setLoadingCompetitions] = useState(false);
  const [competitionsPagination, setCompetitionsPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false
  });

  // جلب قائمة المسابقات التي سجل فيها الطالب فقط
  const fetchCompetitions = async (page = 1) => {
    setLoadingCompetitions(true);
    try {
      const response = await api.get(`/competitions/my-competitions?page=${page}&limit=10`);
      if (response.data.success) {
        // البيانات تأتي في competitions array داخل data
        const allCompetitions = response.data.data.competitions || [];
        
        // فلترة المسابقات لإخفاء المكتملة (completed) والملغاة (cancelled)
        const filteredCompetitions = allCompetitions.filter(
          comp => comp.status !== 'completed' && comp.status !== 'cancelled'
        );
        setCompetitions(filteredCompetitions);
        
        // تحديث pagination من الاستجابة
        const paginationData = response.data.data.pagination;
        setCompetitionsPagination({
          currentPage: paginationData.page,
          totalPages: paginationData.pages,
          totalCount: paginationData.total,
          limit: paginationData.limit,
          hasNextPage: paginationData.page < paginationData.pages,
          hasPrevPage: paginationData.page > 1
        });
      }
    } catch (error) {
      console.error('Error fetching competitions:', error);
      toast.error(t('competitions.fetchError'));
    } finally {
      setLoadingCompetitions(false);
    }
  };

  // جلب البيانات عند تحميل المكون
  useEffect(() => {
    fetchCompetitions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // التعامل مع تغيير الصفحة
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= competitionsPagination.totalPages) {
      fetchCompetitions(newPage);
    }
  };

  // تنسيق التاريخ بالميلادي فقط
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // تنسيق حالة المسابقة
  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return t('competitions.active');
      case 'upcoming':
        return t('competitions.upcoming');
      case 'completed':
        return t('competitions.completed');
      case 'cancelled':
        return t('competitions.cancelled');
      case 'draft':
        return t('competitions.draft');
      default:
        return status;
    }
  };


  return (
    <div className="mx-auto w-full max-w-6xl space-y-5 px-3 pb-8 sm:space-y-6 sm:px-4 sm:pb-10" dir={isRTL ? 'rtl' : 'ltr'}>
      <DailyExercisesHero
        title={t('navigation.competitions')}
        description={t('competitions.studentPageDescription')}
        icon={TrophyIcon}
        iconClassName="text-amber-600"
      />

      <div className="space-y-5 sm:space-y-6">
        {loadingCompetitions ? (
          <div className="rounded-[1.35rem] border-[3px] border-sky-200/80 bg-gradient-to-br from-white to-sky-50/80 px-6 py-14 text-center shadow-[0_8px_0_0_rgba(14,165,233,0.1)]">
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-[3px] border-sky-200 border-t-sky-600" aria-hidden />
            <p className="mt-4 text-sm font-bold text-slate-600 sm:text-base">{t('common.loading')}</p>
          </div>
        ) : competitions.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 lg:gap-6">
              {competitions.map((competition, index) => (
                <div
                  key={index}
                  className="overflow-hidden rounded-[1.35rem] border-[3px] border-slate-200/90 bg-white shadow-[0_8px_0_0_rgba(15,23,42,0.06)] transition-shadow hover:shadow-md"
                >
                  <div className={`p-5 sm:p-6 ${
                    competition.status === 'active' ? 'bg-gradient-to-br from-emerald-500 to-emerald-700' :
                    competition.status === 'upcoming' ? 'bg-gradient-to-br from-sky-500 to-indigo-600' :
                    competition.status === 'completed' ? 'bg-gradient-to-br from-slate-500 to-slate-700' :
                    'bg-gradient-to-br from-amber-500 to-orange-600'
                  }`}>
                    <div className="mb-3 flex items-start justify-between gap-2">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border-2 border-white/30 bg-white/15">
                        <TrophyIcon className="h-7 w-7 text-white" strokeWidth={2.25} />
                      </div>
                      <span className={`inline-flex rounded-full bg-white/95 px-3 py-1 text-xs font-extrabold ${
                        competition.status === 'active' ? 'text-emerald-800' :
                        competition.status === 'upcoming' ? 'text-sky-800' :
                        competition.status === 'completed' ? 'text-slate-800' :
                        'text-amber-900'
                      }`}>
                        {getStatusLabel(competition.status)}
                      </span>
                    </div>
                    <h3 className="mb-2 line-clamp-2 text-lg font-black text-white sm:text-xl">
                      {competition.name || 'N/A'}
                    </h3>
                    {competition.description && (
                      <p className="line-clamp-2 text-sm font-semibold text-white/90">
                        {competition.description}
                      </p>
                    )}
                  </div>

                  <div className="space-y-4 p-5 sm:p-6">
                    <div className="flex items-center justify-between text-sm font-semibold">
                      <span className="text-slate-500">{t('competitions.totalRounds')}</span>
                      <span className="tabular-nums text-slate-900">
                        {competition.totalRounds || 0} {t('competitions.rounds')}
                      </span>
                    </div>

                    <div className="flex items-start gap-2 text-sm">
                      <CalendarIcon className="mt-0.5 h-4 w-4 shrink-0 text-sky-500" strokeWidth={2.25} />
                      <div className="min-w-0 flex-1">
                        <span className="font-semibold text-slate-500">{t('competitions.registrationStart')}</span>
                        <p className="font-bold tabular-nums text-slate-900">{formatDate(competition.schedule?.registrationStart)}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2 text-sm">
                      <CalendarIcon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" strokeWidth={2.25} />
                      <div className="min-w-0 flex-1">
                        <span className="font-semibold text-slate-500">{t('competitions.competitionStart')}</span>
                        <p className="font-bold tabular-nums text-slate-900">{formatDate(competition.schedule?.competitionStart)}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between border-t border-slate-100 pt-3">
                      <span className="text-sm font-semibold text-slate-500">{t('competitions.maxParticipants')}</span>
                      <span className="text-lg font-black tabular-nums text-sky-600">
                        {competition.settings?.maxParticipants || 0}
                      </span>
                    </div>

                    <div className="pt-1">
                      {competition.status === 'active' && (
                        <button
                          type="button"
                          onClick={() => navigate(`/student/competitions/${competition.competitionId}`)}
                          className={`flex w-full min-h-[2.75rem] items-center justify-center gap-2 rounded-2xl border-[3px] border-emerald-800/25 bg-gradient-to-b from-emerald-400 to-emerald-600 px-4 py-3 text-sm font-black text-white shadow-[0_3px_0_0_rgba(6,95,70,0.35)] transition hover:brightness-105 ${isRTL ? 'flex-row-reverse' : ''}`}
                        >
                          <PlayIcon className="h-5 w-5 shrink-0" strokeWidth={2.5} />
                          {t('competitions.joinCompetition')}
                        </button>
                      )}
                      {competition.status === 'completed' && (
                        <button
                          type="button"
                          onClick={() => navigate(`/student/competitions/${competition.competitionId}`)}
                          className={`flex w-full min-h-[2.75rem] items-center justify-center gap-2 rounded-2xl border-[3px] border-slate-400/40 bg-gradient-to-b from-slate-500 to-slate-700 px-4 py-3 text-sm font-black text-white shadow-[0_3px_0_0_rgba(15,23,42,0.25)] transition hover:brightness-105 ${isRTL ? 'flex-row-reverse' : ''}`}
                        >
                          <CheckCircleIcon className="h-5 w-5 shrink-0" strokeWidth={2.5} />
                          {t('competitions.view')}
                        </button>
                      )}
                      {(competition.status === 'upcoming' || competition.status === 'draft') && (
                        <button
                          type="button"
                          onClick={() => navigate(`/student/competitions/${competition.competitionId}`)}
                          className={`flex w-full min-h-[2.75rem] items-center justify-center gap-2 rounded-2xl border-[3px] border-sky-700/25 bg-gradient-to-b from-sky-400 to-sky-600 px-4 py-3 text-sm font-black text-white shadow-[0_3px_0_0_rgba(12,74,110,0.3)] transition hover:brightness-105 ${isRTL ? 'flex-row-reverse' : ''}`}
                        >
                          <CalendarIcon className="h-5 w-5 shrink-0" strokeWidth={2.5} />
                          {t('competitions.view')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {competitionsPagination.totalPages > 1 && (
              <div className="flex flex-col gap-3 rounded-[1.35rem] border-[3px] border-slate-200/90 bg-slate-50/50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                <p className="text-center text-sm font-semibold text-slate-600 sm:text-start">
                  {t('common.showing')}{' '}
                  <span className="font-black tabular-nums">{((competitionsPagination.currentPage - 1) * competitionsPagination.limit) + 1}</span>
                  {' '}{t('common.to')}{' '}
                  <span className="font-black tabular-nums">{Math.min(competitionsPagination.currentPage * competitionsPagination.limit, competitionsPagination.totalCount)}</span>
                  {' '}{t('common.of')}{' '}
                  <span className="font-black tabular-nums">{competitionsPagination.totalCount}</span>
                  {' '}{t('competitions.results')}
                </p>
                <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-end">
                  <button
                    type="button"
                    onClick={() => handlePageChange(competitionsPagination.currentPage - 1)}
                    disabled={!competitionsPagination.hasPrevPage}
                    className="inline-flex min-h-[2.5rem] items-center rounded-xl border-2 border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {t('common.previous')}
                  </button>
                  <span className="inline-flex min-h-[2.5rem] items-center rounded-xl border-2 border-slate-200 bg-white px-4 text-sm font-bold tabular-nums text-slate-700">
                    {t('common.page')} {competitionsPagination.currentPage} {t('common.of')} {competitionsPagination.totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => handlePageChange(competitionsPagination.currentPage + 1)}
                    disabled={!competitionsPagination.hasNextPage}
                    className="inline-flex min-h-[2.5rem] items-center rounded-xl border-2 border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {t('common.next')}
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="rounded-[1.35rem] border-[3px] border-slate-200/90 bg-gradient-to-br from-white to-amber-50/50 px-6 py-14 text-center shadow-[0_8px_0_0_rgba(245,158,11,0.1)]">
            <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-[1.25rem] border-[3px] border-amber-200 bg-amber-50">
              <TrophyIcon className="h-11 w-11 text-amber-500" strokeWidth={2} />
            </div>
            <h3 className="text-xl font-black text-slate-900 sm:text-2xl">
              {t('competitions.noCompetitions')}
            </h3>
            <p className="mx-auto mt-2 max-w-md text-sm font-semibold leading-relaxed text-slate-600 sm:text-base">
              {t('competitions.studentEmptyHint')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Competitions;

